import { useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { inputClass } from '@/presentation/component/formStyles';

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

interface PasswordVisibilityToggleProps {
  isVisible: boolean;
  disabled?: boolean;
  fieldId?: string;
  onToggle: () => void;
}

const PasswordVisibilityToggle = ({
  isVisible,
  disabled,
  fieldId,
  onToggle,
}: PasswordVisibilityToggleProps) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-controls={fieldId}
      aria-label={isVisible ? t('common.hidePassword') : t('common.showPassword')}
      className={
        'absolute inset-y-0 right-0 flex items-center rounded-lg px-3 ' +
        'text-[var(--color-ink-faint)] transition-colors hover:text-[var(--color-ink)] ' +
        'outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ' +
        'disabled:cursor-not-allowed disabled:opacity-50'
      }
    >
      {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
};

export const InputField = ({
  label,
  error,
  id,
  type,
  className = '',
  ...rest
}: InputFieldProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const fieldId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && isPasswordVisible ? 'text' : type;

  const handleTogglePasswordVisibility = () => setIsPasswordVisible((isVisible) => !isVisible);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={fieldId} className="text-sm font-medium text-[var(--color-ink-soft)]">
          {label}
          {rest.required && <span className="ml-0.5 text-[var(--color-bad)]">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={fieldId}
          type={inputType}
          className={`${inputClass(!!error)} ${isPasswordField ? 'pr-10' : ''} ${className}`}
          {...rest}
        />
        {isPasswordField && (
          <PasswordVisibilityToggle
            isVisible={isPasswordVisible}
            disabled={rest.disabled}
            fieldId={fieldId}
            onToggle={handleTogglePasswordVisibility}
          />
        )}
      </div>
      {error && <p className="text-xs text-[var(--color-bad)]">{error}</p>}
    </div>
  );
};
